import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { router, useLocalSearchParams } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import PageHeader from '@/components/PageHeader';
import { profileService } from '@/services/profileService';

export default function SponsorPhone() {
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const {
    sponsorId,
    sponsorName,
    sponsorPhotoUrl,
    sponsorRole,
    scheduledDate,
    scheduledTime,
    existingPhone,
    amountMinor,
    currency,
    sessionDurationMinutes,
  } = useLocalSearchParams<{
    sponsorId: string;
    sponsorName: string;
    sponsorPhotoUrl?: string;
    sponsorRole?: string;
    scheduledDate: string;
    scheduledTime: string;
    existingPhone?: string;
    amountMinor?: string;
    currency?: string;
    sessionDurationMinutes?: string;
  }>();

  const [phone, setPhone] = useState('');
  const [originalPhone, setOriginalPhone] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill phone if user already has one
  useEffect(() => {
    if (existingPhone) {
      console.log('📞 Received existingPhone:', existingPhone);

      // Check if already in formatted format (11) 99999-9999
      if (existingPhone.includes('(') && existingPhone.includes(')')) {
        // Already formatted, use as-is
        setPhone(existingPhone);
        setOriginalPhone(existingPhone);
        console.log('📞 Pre-filled phone (already formatted):', existingPhone);
      } else {
        // Convert from +5511999999999 to (11) 99999-9999 format
        const numbers = existingPhone.replace(/\D/g, '');
        // Remove country code (55) if present
        const localNumbers = numbers.startsWith('55') ? numbers.slice(2) : numbers;
        if (localNumbers.length === 11) {
          const formatted = `(${localNumbers.slice(0, 2)}) ${localNumbers.slice(2, 7)}-${localNumbers.slice(7, 11)}`;
          setPhone(formatted);
          setOriginalPhone(existingPhone);
          console.log('📞 Pre-filled phone (converted):', formatted);
        } else if (localNumbers.length > 0) {
          // Partial number, try to format what we have
          const formatted = formatPhoneInput(localNumbers);
          setPhone(formatted);
          setOriginalPhone(existingPhone);
          console.log('📞 Pre-filled phone (partial):', formatted);
        }
      }
    }
  }, [existingPhone]);

  const formatPhoneInput = (text: string) => {
    // Remove all non-digit characters
    const numbers = text.replace(/\D/g, '');

    // Format as ( __ ) _____ - ____
    if (numbers.length === 0) return '';
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneInput(text);
    setPhone(formatted);
  };

  const validatePhone = (phoneStr: string): boolean => {
    const numbers = phoneStr.replace(/\D/g, '');
    return numbers.length === 11; // Brazilian phone: (11) 99999-9999
  };

  const handleConfirm = async () => {
    if (!validatePhone(phone)) {
      Alert.alert('Erro', t('sponsors.errors.invalidPhone'));
      return;
    }

    try {
      setLoading(true);

      // The phone state is already in formatted display format: (11) 96000-8963
      // This is the format we want to save to profile
      const formattedPhoneForProfile = phone;

      // Normalize original phone to display format for comparison
      let originalFormatted = originalPhone;
      if (originalPhone && !originalPhone.includes('(')) {
        // Convert +5511960008963 to (11) 96000-8963 for comparison
        const origNumbers = originalPhone.replace(/\D/g, '');
        const origLocal = origNumbers.startsWith('55') ? origNumbers.slice(2) : origNumbers;
        if (origLocal.length === 11) {
          originalFormatted = `(${origLocal.slice(0, 2)}) ${origLocal.slice(2, 7)}-${origLocal.slice(7, 11)}`;
        }
      }

      // Update profile if phone number changed (compare display formats)
      if (formattedPhoneForProfile !== originalFormatted) {
        console.log('📞 Phone changed, updating profile...');
        console.log('📞 Old:', originalFormatted, '-> New:', formattedPhoneForProfile);
        const updateResult = await profileService.updatePhone(formattedPhoneForProfile);
        if (!updateResult.success) {
          throw new Error('PROFILE_PHONE_UPDATE_FAILED');
        }
      } else {
        console.log('📞 Phone unchanged, skipping profile update');
      }

      router.push({
        pathname: '/sponsors/payment',
        params: {
          sponsorId,
          sponsorName,
          sponsorPhotoUrl: sponsorPhotoUrl || '',
          sponsorRole: sponsorRole || '',
          scheduledDate,
          scheduledTime,
          amountMinor: amountMinor || '',
          currency: currency || '',
          sessionDurationMinutes: sessionDurationMinutes || '',
        },
      });
    } catch (err: any) {
      console.error('Error confirming appointment phone:', err);
      Alert.alert(t('sponsors.errors.title'), t('sponsors.phone.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeContainer} edges={['top']}>
      <PageHeader
        title={t('sponsors.phone.headerTitle') || 'Contato'}
        onBack={() => router.back()}
      />
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.content}>
          {/* Title and Description */}
          <View style={styles.textContainer}>
            <Text style={styles.title}>{t('sponsors.phone.title')}</Text>
            <Text style={styles.subtitle}>{t('sponsors.phone.subtitle')}</Text>
          </View>

          {/* Phone Input */}
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              value={phone}
              onChangeText={handlePhoneChange}
              placeholder={t('sponsors.phone.placeholder')}
              placeholderTextColor='#A19F97'
              keyboardType='phone-pad'
              maxLength={16} // (11) 99999-9999
              autoFocus
            />
          </View>
        </View>

        {/* Bottom Button */}
        <View style={[styles.bottomSection, { paddingBottom: Math.max(insets.bottom, 8) }]}>
          <TouchableOpacity
            style={[styles.confirmButton, loading && styles.confirmButtonDisabled]}
            onPress={handleConfirm}
            disabled={loading}
          >
            <Text style={styles.confirmButtonText}>
              {loading ? t('sponsors.phone.confirming') : t('sponsors.phone.confirmButton')}
            </Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 24,
  },
  textContainer: {
    gap: 8,
  },
  title: {
    fontSize: 24,
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    lineHeight: 28.8,
  },
  subtitle: {
    fontSize: 14,
    color: '#757C85',
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
  },
  inputContainer: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 16,
    minHeight: 56,
  },
  input: {
    fontSize: 14,
    color: '#151515',
    fontFamily: 'Inter_500Medium',
  },
  bottomSection: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 24,
  },
  confirmButton: {
    backgroundColor: '#151515',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 56,
  },
  confirmButtonDisabled: {
    opacity: 0.6,
  },
  confirmButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    lineHeight: 19.6,
  },
});
